import { SyncOutlined, BulbOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useQueryParams from "@/utils/use-query-params";
import {
  chargebackService,
  vendorCentralCredentialService,
} from "@/services";
import { useObservable } from "@/utils/use-observable";
import { ColumnsType } from "antd/lib/table";
import { Chargeback } from "@/models/chargeback";
import { chargebackStatusList } from "@/models/enum/chargeback-status";
import {
  apiDateTimeFormat,
  dateFormat,
  renderCurrency,
  renderDate,
  renderStatus,
  tokenSeparator,
} from "@/utils";
import "./index.less";
import dayjs from "dayjs";
import { TableRef } from "antd/es/table";
import { vendorCentralStatusList as vendorCentralChargebackStatusList } from "@/models/enum/vendor-central-chargeback-status";
import { chargebackIssueTypeList } from "@/models/enum/issue-type";
import { VendorCentralStatusHistoryModal } from "@/app/components/vendor-central-status-history-modal-chargeback";
import { SuggestionsModal } from "@/app/components/suggestions-modal";
import { ChargebackSuggestionsResponse } from "@/models/chargeback-suggestions";

const { Title, Text } = Typography;

function VCStatusWithHistory(props: {
  status?: string;
  statusHistory?: Array<{ status: string; date: string }>;
}) {
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const hasHistory = props.statusHistory && props.statusHistory.length > 0;

  if (hasHistory || props.status) {
    return (
      <>
        <Text
          style={{ cursor: hasHistory ? "pointer" : "default", textDecoration: hasHistory ? "underline" : "none" }}
          onClick={() => hasHistory && setHistoryModalVisible(true)}
        >
          {props.status || "-"}
        </Text>
        {historyModalVisible && (
          <VendorCentralStatusHistoryModal
            visible={historyModalVisible}
            onClose={() => setHistoryModalVisible(false)}
            statusHistory={props.statusHistory}
            currentStatus={props.status}
          />
        )}
      </>
    );
  }

  return <Text>{props.status || "-"}</Text>;
}

export default function ChargebacksPage() {
  const filters = useObservable(chargebackService.filters);
  const chargebacks = useObservable(chargebackService.chargebacks);
  const isFetching = useObservable(chargebackService.isFetching);
  const allAccounts = useObservable(vendorCentralCredentialService.allAccounts);
  const tableRef = useRef<TableRef>(null);
  const query = useQueryParams();
  const filtersInitialized = useRef(false);
  const [suggestionsModalVisible, setSuggestionsModalVisible] = useState(false);
  const [suggestionsContent, setSuggestionsContent] = useState<ChargebackSuggestionsResponse | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const items: CollapseProps["items"] = [
    {
      key: "filters",
      label: "Filters",
      children: (
        <Row gutter={[16, 16]}>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.chargebackIds}
              onChange={chargebackService.setChargebackIds}
              placeholder="Chargeback IDs"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <DatePicker
              format={dateFormat()}
              value={
                filters.dateFrom
                  ? dayjs(filters.dateFrom, apiDateTimeFormat)
                  : null
              }
              onChange={chargebackService.setDateFrom}
              placeholder="Creation Date from"
              style={{ width: "100%" }}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <DatePicker
              format={dateFormat()}
              value={
                filters.dateTo
                  ? dayjs(filters.dateTo, apiDateTimeFormat)
                  : null
              }
              onChange={chargebackService.setDateTo}
              placeholder="Creation Date to"
              style={{ width: "100%" }}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              options={allAccounts}
              optionFilterProp="label"
              value={filters.accountNames}
              onChange={chargebackService.setAccountNames}
              placeholder="Account Names"
              allowClear
              style={{ width: "100%" }}
              mode="multiple"
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.payeeCodes}
              onChange={chargebackService.setPayeeCodes}
              placeholder="Payee Codes"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              options={chargebackStatusList}
              value={filters.statuses}
              mode="multiple"
              onChange={chargebackService.setStatuses}
              allowClear
              placeholder="Statuses"
              style={{ width: "100%" }}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              options={vendorCentralChargebackStatusList}
              value={filters.vendorCentralStatuses}
              onChange={chargebackService.setVendorCentralStatuses}
              placeholder="V.C. Statuses"
              allowClear
              style={{ width: "100%" }}
              mode="multiple"
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              options={chargebackIssueTypeList}
              value={filters.issueTypes}
              onChange={chargebackService.setIssueTypes}
              placeholder="Issue Types"
              allowClear
              style={{ width: "100%" }}
              mode="multiple"
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.eans}
              onChange={chargebackService.setEans}
              placeholder="EANs"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.asins}
              onChange={chargebackService.setAsins}
              placeholder="ASINs"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.purchaseOrders}
              onChange={chargebackService.setPurchaseOrders}
              placeholder="Purchase Orders"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.invoiceNumbers}
              onChange={chargebackService.setInvoiceNumbers}
              placeholder="Invoice Numbers"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
          <Col xl={4} md={6} sm={12} xs={24}>
            <Select
              value={filters.reversalInvoiceNumbers}
              onChange={chargebackService.setReversalInvoiceNumbers}
              placeholder="Invoice Reversal Numbers"
              allowClear
              style={{ width: "100%" }}
              mode="tags"
              tokenSeparators={tokenSeparator}
              open={false}
              suffixIcon={false}
            />
          </Col>
        </Row>
      ),
    },
  ];

  const columns: ColumnsType<Chargeback> = [
    {
      title: "Chargeback ID",
      key: "chargebackId",
      dataIndex: "chargebackId",
      width: "160px",
      render: (value: string, record: Chargeback) => (
        <Link to={`/chargebacks/${record.id}`}>{value}</Link>
      ),
    },
    {
      title: "Creation Date",
      key: "creationDate",
      dataIndex: "creationDate",
      width: "140px",
      render: renderDate,
    },
    {
      title: "Account Name",
      key: "accountName",
      dataIndex: "accountName",
      width: "220px",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      width: "130px",
      render: renderStatus(chargebackStatusList),
    },
    {
      title: "V.C. Status",
      key: "vendorCentralStatus",
      dataIndex: "vendorCentralStatus",
      width: "180px",
      render: (status: string, record: Chargeback) => (
        <VCStatusWithHistory
          status={status}
          statusHistory={record.vendorCentralStatusHistory}
        />
      ),
    },
    {
      title: "Issue Type",
      key: "issueType",
      dataIndex: "issueType",
      width: "220px",
    },
    {
      title: "Financial Charge",
      key: "financialCharge",
      dataIndex: "financialCharge",
      align: "right",
      width: "150px",
      render: renderCurrency,
    },
    {
      title: "Purchase Order",
      key: "purchaseOrderNumber",
      dataIndex: "purchaseOrderNumber",
      width: "160px",
      render: (value: string | undefined) => value ?? "-",
    },
    {
      title: "ASIN",
      key: "asin",
      dataIndex: "asin",
      width: "120px",
      render: (value: string | undefined) => value ?? "-",
    },
    {
      title: "Dispute Deadline",
      key: "disputeDateEnd",
      dataIndex: "disputeDateEnd",
      width: "150px",
      render: renderDate,
    }
  ];

  const onScroll = (event: any) => {
    if (event.target) {
      const maxScroll = Math.round(
        event.target.scrollHeight - event.target.clientHeight
      );
      const currentScroll = Math.round(event.target.scrollTop);
      if (currentScroll >= maxScroll - 100) {
        chargebackService.next();
      }
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      const tbody =
        tableRef.current.nativeElement.querySelector(".ant-table-body");
      if (tbody) {
        tbody.addEventListener("scroll", onScroll);

        return () => {
          tbody.removeEventListener("scroll", onScroll);
        };
      }
    }
  }, []);

  useEffect(() => {
    vendorCentralCredentialService.getVendorCentralCredentials();
  }, []);

  useEffect(() => {
    if (filtersInitialized.current) {
      return;
    }

    const payeeCode = query.get("payeeCode");
    const invoiceNumber = query.get("invoiceNumber");
    const reversalInvoiceNumber = query.get("reversalInvoiceNumber");

    if (payeeCode || invoiceNumber || reversalInvoiceNumber) {
      filtersInitialized.current = true;

      if (payeeCode) {
        chargebackService.setPayeeCodes([payeeCode]);
      }

      if (invoiceNumber) {
        chargebackService.setInvoiceNumbers([invoiceNumber]);
      }

      if (reversalInvoiceNumber) {
        chargebackService.setReversalInvoiceNumbers([reversalInvoiceNumber]);
      }
    }
  }, [query]);

  useEffect(() => {
    const subscription = chargebackService.filters.subscribe(() => {
      tableRef.current?.nativeElement
        .querySelector(".ant-table-body")
        ?.scrollTo({ top: 0 });
      chargebackService.getChargebacks();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const onRefresh = () => {
    chargebackService.refresh();
  };

  const onExportCsv = () => {
    chargebackService.exportChargebacks();
  };

  const onSuggestions = async () => {
    setIsLoadingSuggestions(true);
    try {
      const content = await chargebackService.getSuggestions();
      if (content) {
        setSuggestionsContent(content);
        setSuggestionsModalVisible(true);
      }
    } catch (e) {
      console.error("Error fetching suggestions:", e);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  return (
    <div className="chargebacks-page">
      <Title level={3}>Chargebacks</Title>
      <Row className="actions" justify={"space-between"}>
        <Space>
          <Button icon={<SyncOutlined spin={isFetching} />} onClick={onRefresh}>
            Refresh
          </Button>
          <Button icon={<CloudDownloadOutlined />} onClick={onExportCsv}>
            Export
          </Button>
          <Button
            icon={<BulbOutlined />}
            onClick={onSuggestions}
            loading={isLoadingSuggestions}
          >
            Suggestions
          </Button>
        </Space>
      </Row>
      {suggestionsModalVisible && (
        <SuggestionsModal
          visible={suggestionsModalVisible}
          onClose={() => setSuggestionsModalVisible(false)}
          content={suggestionsContent}
        />
      )}
      <Collapse
        className="filters"
        size="small"
        bordered={false}
        items={items}
      />
      <Table
        ref={tableRef}
        columns={columns}
        dataSource={chargebacks}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: "calc(100vh - 233px)" }}
      />
    </div>
  );
}
